import {
  LanguageCode,
  ResolveTranslationOptions,
  SUPPORTED_LANGUAGES,
  TranslateParams,
} from '../types/i18n/i18n.type';
import { baseMessages } from '../translations/index';
import {
  clientTypeCountryOverrides,
  clientTypeOverrides,
} from '../translations/overrides';

/**
 * Storage key for persisting the selected language in localStorage.
 * @internal
 */
export const LANGUAGE_STORAGE_KEY = 'comos.language';

/**
 * Determines the initial language for the application.
 *
 * Resolution order:
 * 1. Language stored in localStorage (if valid)
 * 2. Browser language preference (en → 'en-US', es → 'es-CL')
 * 3. Default fallback to 'es-CL'
 *
 * @returns The initial language code to use
 */
export const determineInitialLanguage = (): LanguageCode => {
  if (typeof window === 'undefined') {
    return 'es-CL';
  }

  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored as LanguageCode)) {
    return stored as LanguageCode;
  }

  const navigatorLang = window.navigator.language;
  if (navigatorLang.startsWith('en')) {
    return 'en-US';
  }
  if (navigatorLang.startsWith('es')) {
    return 'es-CL';
  }

  return 'es-CL';
};

/**
 * Interpolates placeholder values into a translation string.
 *
 * Replaces placeholders in the format `{key}` with corresponding values
 * from the params object. Missing or undefined values are replaced with empty strings.
 *
 * @param text - The translation string with placeholders
 * @param params - Object containing placeholder key-value pairs
 * @returns The interpolated string with placeholders replaced
 *
 * @example
 * ```ts
 * interpolate('Hello {name}', { name: 'Alice' }); // 'Hello Alice'
 * interpolate('Value: {count}', { count: 42 }); // 'Value: 42'
 * ```
 */
export const interpolate = (text: string, params?: TranslateParams): string => {
  if (!params) {
    return text;
  }
  return text.replace(/\{(\w+)\}/g, (_, key) => {
    const value = params[key];
    return value !== undefined ? String(value) : '';
  });
};

/**
 * Resolves a translation value from the translation catalog.
 *
 * Resolution order:
 * 1. Country-specific client-type override
 * 2. Client-type-specific override
 * 3. Base message for the language
 * 4. The translation key itself (as fallback)
 *
 * Supports client-type-specific vocabulary and vertical-specific terminology.
 *
 * @param language - The language code to resolve translations for
 * @param key - The translation key in dot-notation (e.g., 'common.hello')
 * @param options - Options for override and client-type handling
 * @returns The resolved translation string or the key if not found
 *
 * @example
 * ```ts
 * resolveTranslationValue('es-CL', 'common.hello'); // 'Hola'
 * resolveTranslationValue('en-US', 'common.hello'); // 'Hello'
 * ```
 */
export const resolveTranslationValue = (
  language: string,
  key: string,
  options?: ResolveTranslationOptions
): string => {
  const normalizedLanguage = SUPPORTED_LANGUAGES.includes(
    language as LanguageCode
  )
    ? (language as LanguageCode)
    : 'es-CL';

  const override =
    options?.allowClientOverrides && options.clientType
      ? clientTypeOverrides[normalizedLanguage]?.[options.clientType]?.[key]
      : undefined;
  const countryOverride =
    options?.allowClientOverrides &&
    options.clientType &&
    options.tenantCountryCode
      ? clientTypeCountryOverrides[normalizedLanguage]?.[options.clientType]?.[
          options.tenantCountryCode
        ]?.[key]
      : undefined;
  const base = baseMessages[normalizedLanguage]?.[key];

  return countryOverride ?? override ?? base ?? key;
};
